import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Button,
    IconButton,
    ListSubheader,
    Menu,
    MenuItem,
    styled,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import i18next from "i18next";
import { AutocompleteTranslations } from "./objects/autocompleteTranslations";
import { AppState } from "../../reducers";
import CopyIcon from "@mui/icons-material/CopyAll";
import HelpIcon from '@mui/icons-material/HelpOutline';
import LightTooltip from "../utils/tooltip/lightTooltip";
import { determineVariableGroup } from "./utils/mailTemplateVariable";

type Props = {
    onChoose: (variable: string) => void,
    disableCopy?: boolean
}

export function MailTemplateVariablePicker(props: Props): JSX.Element {
    const { t } = useTranslation();
    const autocomplete = useSelector((state: AppState) => state.mailTemplate.slice.autocomplete);
    const action = useSelector((state: AppState) => state.mailTemplate.others.create_action);
    const fields = useMemo(() => {
        return autocomplete.state === 'success' && action !== null ?
            (autocomplete.data.relations[action]?.fields ?? []) :
            [];
    }, [
        autocomplete.state === 'success' && action ?
            autocomplete.data.relations[action]?.fields :
            null,
    ]);
    const translations = useMemo(() => {
        return autocomplete.state === 'success' ?
            autocomplete.data.translations :
            { action: {}, fields: {}, recipients: {} } as AutocompleteTranslations;
    }, [
        autocomplete.state === 'success' ?
            autocomplete.data.translations :
            null
    ]);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const anchorElement = useRef(null);
    const groupedVariables = useMemo(() => {
        const getVariableTranslation = (variable: string) => {
            const field = translations.fields[variable] || {};
            return field[i18next.language] || Object.values(field)[0] || '';
        };

        const compareVariables = (a: any, b: any) => getVariableTranslation(a.variable).localeCompare(getVariableTranslation(b.variable));


        const groupedVariables = fields
            .map((variable) => ({
                variable,
                group: determineVariableGroup(variable),
                translation: getVariableTranslation(variable),
            }))
            .filter((item) => item.translation.includes(search.toLowerCase()) || t(`shared.${item.group}`).includes(search.toLowerCase()))
            .sort((a, b) => t(a.group).localeCompare(t(b.group)));

        let start = 0;
        for (let i = 1; i <= groupedVariables.length; i++) {
            if (i === groupedVariables.length || groupedVariables[i]?.group !== groupedVariables[start]?.group) {
                groupedVariables
                    .slice(start, i)
                    .sort(compareVariables)
                    .forEach((item, index) => groupedVariables[start + index] = item);
                start = i;
            }
        }

        return groupedVariables;
    }, [fields, translations, i18next.language, search]);

    // const variables = (
    //     fields.map((variable) => {
    //         const field = translations.fields[variable];

    //         if (field) {
    //             return { translation: field[i18next.language], variable };
    //         }

    //         return null;
    //     }).filter((item) => {
    //         return item?.translation && item.translation.includes(search.toLowerCase());
    //     }) as { translation: string, variable: string }[]
    // ).sort((a, b) => a.translation.localeCompare(b.translation));

    const onClose = () => {
        setOpen(false);
    };

    const onChoose = (variable: string) => {
        props.onChoose(variable);
        onClose();
    };

    const onCopy = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, variable: string) => {
        event.stopPropagation();
        const content = `{{${variable}}}`;
        navigator.clipboard.writeText(content);
        onClose();
    };

    return (
        <div>

            <Typography>
                {t('shared.mail-template-variables')}
                <LightTooltip title={t('shared.mail-template-variables-help')}>
                    <HelpIcon className={'ft-role-info'} />
                </LightTooltip>
            </Typography>
            <MenuButton
                variant="contained"
                ref={anchorElement}
                onClick={() => setOpen(true)}
            >
                {t('shared.mail-template-variables-open')}
            </MenuButton>
            <Menu
                open={open}
                anchorEl={anchorElement.current}
                PaperProps={{
                    style: {
                        maxHeight: 450
                    }
                }}
                onClose={onClose}
            >
                <MenuItem
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: '#fff !important'
                    }}
                >
                    <TextField
                        label={t('shared.search')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        variant="outlined"
                        fullWidth
                    />
                </MenuItem>
                {/* {
                    variables.map(({ translation, variable }) => (
                        <MenuItem
                            key={variable}
                            onClick={() => onChoose(variable)}
                            sx={{ justifyContent: 'space-between' }}
                        >
                            {translation}
                            {
                                !props.disableCopy &&
                                <Tooltip title={t('shared.mail-template-variables-copy')}>
                                    <IconButton onClick={(event) => onCopy(event, variable)}>
                                        <CopyIcon />
                                    </IconButton>
                                </Tooltip>
                            }
                        </MenuItem>
                    ))
                } */}
                {groupedVariables.map(({ translation, variable, group }, index) => {
                    return [
                        index === 0 || groupedVariables[index - 1]?.group !== group ? (
                            <ListSubheader key={'list-subheader-' + variable} sx={{ fontWeight: '900', textTransform: 'uppercase', top: '64px' }}>
                                {t(`shared.${group}`)}
                            </ListSubheader>
                        ) : null,
                        <MenuItem
                            key={'menu-item-' + variable}
                            onClick={() => onChoose(variable)}
                            sx={{ justifyContent: 'space-between', marginLeft: 2 }}
                        >
                            {translation}
                            {!props.disableCopy && (
                                <Tooltip title={t('shared.mail-template-variables-copy')}>
                                    <IconButton onClick={(event) => onCopy(event, variable)}>
                                        <CopyIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MenuItem>
                    ];
                })}
                {
                    groupedVariables.length === 0 &&
                    <MenuItem disabled>
                        <Typography>
                            {t('shared.mail-template-variables-none-available')}
                        </Typography>
                    </MenuItem>
                }
            </Menu>
        </div>
    );
}

const MenuButton = styled(Button)((props) => ({
    marginTop: props.theme.spacing(2),
    marginBottom: props.theme.spacing(2),
}));